import { useState, useEffect } from 'react';
import { LookupApp } from '../types';

type SortColumn = 'title' | 'countries' | 'clicks' | 'fillRate' | 'conversions' | 'conversionRate' | 'revenue';
type SortDirection = 'asc' | 'desc';
type SortTimeframe = 'today' | 'week';

export const useLookupState = () => {
  // Sorting state
  const [sortColumn, setSortColumn] = useState<SortColumn>('clicks');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [sortTimeframe, setSortTimeframe] = useState<SortTimeframe>('today');

  // Load showStats preference from localStorage
  const getInitialShowStats = (): 'overall' | 'matched' => {
    try {
      // First try the new key
      let savedShowStats = localStorage.getItem('lookupShowStats');
      
      // If not found, try migrating from old key
      if (!savedShowStats) {
        savedShowStats = localStorage.getItem('productsLookupShowStats');
        if (savedShowStats) {
          // Migrate to new key and remove old key
          localStorage.setItem('lookupShowStats', savedShowStats);
          localStorage.removeItem('productsLookupShowStats');
        }
      }
      
      if (savedShowStats === 'matched' || savedShowStats === 'overall') {
        return savedShowStats;
      }
    } catch (error) {
      console.error('Error loading showStats from localStorage:', error);
    }
    return 'overall';
  };
  
  const [showStats, setShowStats] = useState<'overall' | 'matched'>(getInitialShowStats());

  // Modal states
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showEditClinkModal, setShowEditClinkModal] = useState(false);
  const [showTrackingLinksModal, setShowTrackingLinksModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [showCreateClinkModal, setShowCreateClinkModal] = useState(false);

  // Selected items for modals
  const [selectedApp, setSelectedApp] = useState<LookupApp | null>(null);
  const [selectedClink, setSelectedClink] = useState<any>(null);
  const [selectedAppForClink, setSelectedAppForClink] = useState<LookupApp | null>(null);
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);
  const [selectedDemandSourceId, setSelectedDemandSourceId] = useState<string | null>(null);

  // Auto-load flag
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  // Save showStats preference to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('lookupShowStats', showStats);
    } catch (error) {
      console.error('Error saving showStats to localStorage:', error);
    }
  }, [showStats]);

  const handleSort = (column: SortColumn, timeframe?: 'today' | 'week') => {
    // If clicking on a metric column's 1d or 7d
    if (timeframe) {
      if (sortColumn === column && sortTimeframe === timeframe) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumn(column);
        setSortTimeframe(timeframe);
        setSortDirection('desc');
      }
    } else {
      // For non-metric columns (title, countries)
      if (sortColumn === column) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortColumn(column);
        setSortDirection('desc');
      }
    }
  };

  // Modal handlers
  const handleShowStats = (app: LookupApp, clink?: any) => {
    setSelectedApp(app);
    setSelectedClink(clink || null); // Always set selectedClink, null if no clink provided
    setShowStatsModal(true);
  };

  const handleCreateClink = (app: LookupApp, campaignId?: string, demandSourceId?: string) => {
    setSelectedAppForClink(app);
    setSelectedCampaignId(campaignId || null);
    setSelectedDemandSourceId(demandSourceId || null);
    setShowCreateClinkModal(true);
  };

  const handleCloseCreateClink = () => {
    setShowCreateClinkModal(false);
    setSelectedAppForClink(null);
    setSelectedCampaignId(null);
    setSelectedDemandSourceId(null);
  };

  const handleEditClink = (clink: any, app?: LookupApp) => {
    setSelectedClink(clink);
    if (app) {
      setSelectedApp(app);
    }
    setShowEditClinkModal(true);
  };

  const handleShowTrackingLinks = (clink: any, app?: LookupApp) => {
    setSelectedClink(clink);
    if (app) {
      setSelectedApp(app);
    }
    setShowTrackingLinksModal(true);
  };

  const handleShowHistory = (clink: any) => {
    setSelectedClink(clink);
    setShowHistoryModal(true);
  };

  return {
    // Sort state
    sortColumn,
    sortDirection, 
    sortTimeframe,
    setSortColumn,
    setSortDirection,
    setSortTimeframe,
    handleSort,

    // Stats preference
    showStats,
    setShowStats,

    // Modal state
    showStatsModal,
    setShowStatsModal,
    showEditClinkModal,
    setShowEditClinkModal,
    showTrackingLinksModal,
    setShowTrackingLinksModal,
    showHistoryModal,
    setShowHistoryModal,
    showCreateClinkModal,
    setShowCreateClinkModal,

    // Selected items
    selectedApp,
    setSelectedApp,
    selectedClink,
    setSelectedClink,
    selectedAppForClink,
    setSelectedAppForClink,
    selectedCampaignId,
    setSelectedCampaignId,
    selectedDemandSourceId,
    setSelectedDemandSourceId,

    // Auto-load flag
    hasInitialLoad,
    setHasInitialLoad,

    // Modal handlers
    handleShowStats,
    handleCreateClink,
    handleCloseCreateClink,
    handleEditClink,
    handleShowTrackingLinks,
    handleShowHistory
  };
};